import { Dispatch, FC, Fragment, SetStateAction } from 'react'
import { FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'
import { useFeatureValue } from '@growthbook/growthbook-react'

import MobileMenuItem from '../MobileUI/MobileMenuItem'
import useCategoryDataStructure from '../../../hooks/useCategoryDataStructure'
import { Catalog } from '../../../../utils/types/Product'
import { useApi } from '../../../../services/useApi'
import { QueryParam, ResourceTag } from '../../../../utils/constants'
import { getProductsCount } from '../../../../lib/product'
import RenderCatalogNode from './RenderCatalogNode'
import CategoryIcon from '../../../Catalog/CategoryIcon'

import styles from '../../../../styles/Header.module.less'

interface MobileCategoryPickerProps {
  handleSelectAndCloseMenus: (categoryCode?: string) => void
  selectedSubCategory: Catalog
  breadcrumb: Catalog[]
  setBreadcrumb: Dispatch<SetStateAction<Catalog[]>>
  setAnimationDirection: Dispatch<SetStateAction<'forward' | 'backward' | null>>
}

const MobileCategoryPicker: FC<MobileCategoryPickerProps> = (props) => {
  const {
    handleSelectAndCloseMenus,
    breadcrumb,
    selectedSubCategory,
    setBreadcrumb,
    setAnimationDirection,
  } = props

  const { query } = useRouter()
  const { getResourceCount } = useApi()
  const categoryMediaVariant = useFeatureValue<string>('category-media-variant', 'none')

  const searchQuery = query[QueryParam.search]?.toString()
  const countQuery = searchQuery ? 'aggregate=index.catalog.id' : 'aggregate=index.catalog.id'
  const { data: productsAggregate } = getResourceCount(ResourceTag.product, countQuery)

  const { selectedCategory, parentNodes } = useCategoryDataStructure()

  const isSelectedCategory = (categoryCode: string) => categoryCode === selectedCategory
  const isAnySubCatActive = (
    categoryCode: string,
  ) => parentNodes.slice(1).find((node) => node.code === categoryCode)

  const handleSubCatMenuItemClick = (
    subCat: Catalog,
  ) => {
    setAnimationDirection('forward')
    setTimeout(() => {
      setBreadcrumb((prev) => [...prev, subCat])
      if (breadcrumb.length > 1) {
        handleSelectAndCloseMenus(subCat['catalog.code'])
      }
    }, 150)
  }

  const setMobileMenuItemActiveStyles = (categoryCode: string) => {
    const classNames = []
    const isOpened = breadcrumb.at(-1)?.['catalog.code'] === categoryCode

    if (!isOpened && isAnySubCatActive(categoryCode)) classNames.push(styles['active-category-label'])

    return classNames.join(' ')
  }

  const getSubCategory = (): Catalog => {
    // Handle rendering the sub-category list when the breadcrumb is 2 level deep
    if (breadcrumb.length > 1) {
      return breadcrumb?.at(-1) || breadcrumb[0]
    }

    return selectedSubCategory
  }

  return (
    <div className={styles['mobile-category-picker-container']}>
      {getSubCategory().catalog?.map((categoryData) => {
        const isSubCatSelected = isSelectedCategory(categoryData['catalog.code'])
        const productsCount = getProductsCount(productsAggregate, categoryData)

        return (
          <Fragment key={categoryData['catalog.code']}>
            <MobileMenuItem
              isActive={isSubCatSelected}
              handleClick={
                productsCount
                  ? () => handleSubCatMenuItemClick(categoryData)
                  : undefined
              }
              additionalClassNames={setMobileMenuItemActiveStyles(categoryData['catalog.code'])}
              hideIcon={breadcrumb.length > 1}
            >
              <FlexboxGrid align="middle">
                {categoryMediaVariant !== 'none' && categoryMediaVariant !== 'top-category-only' && (
                  <CategoryIcon
                    media={categoryData.media}
                    categoryName={categoryData['catalog.label']}
                    size={45}
                    type={categoryMediaVariant === 'images-only' ? 'default' : undefined}
                    className={`${styles['mobile-category-icon']} margin-right-spacer-double`}
                  />
                )}
                <RenderCatalogNode
                  productsCount={productsCount}
                  catalogLabel={categoryData['catalog.label']}
                />
              </FlexboxGrid>
            </MobileMenuItem>
          </Fragment>
        )
      })}
    </div>
  )
}

export default MobileCategoryPicker
